<template>
  <v-container fluid fill-height class="pa-0">
    <v-slide-y-transition mode="out-in">
      <v-layout column align-center style="position: relative">
        
        <v-dialog v-if="hasDisclaimer" v-model="disclaimerDialog" max-width="750" persistent>
          <v-card>
            <v-card-title class="headline">Disclaimer</v-card-title>
            <v-card-text>{{ disclaimer }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="closeDisclaimer">I Understand</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-layout row class="search-filter-container mt-2">
          <v-flex grow xs9 md10 xl11>
            <search />
          </v-flex>
          <v-flex v-if="categories" shrink align-center fill-height xs3 md2 xl1>
            <v-badge v-model="activeCategories.length" color="white" class="mr-2" overlap>
              <template v-slot:badge>
                <span class="text-black" v-text="activeCategories.length" />
              </template>
              <v-btn @click="showCategories = !showCategories" class="mx-0" color="primary">Filter</v-btn>
            </v-badge>
          </v-flex>
        </v-layout>

        <v-bottom-sheet v-model="showStrollPreview" max-width="1000px" inset>
          <stroll-preview
            v-if="activeStroll"
            v-on:hidePreview="showStrollPreview = false"
            :stroll="activeStroll"
          />
        </v-bottom-sheet>

        <v-bottom-sheet v-model="showCategories" max-width="1000px" inset>
          <v-card>
            <v-card-title primary-title>
              <h3>
                <v-icon>filter_list</v-icon>Filter by Category
                <v-btn icon flat small @click="clearActiveCategories">
                  <v-icon>clear</v-icon>
                </v-btn>
              </h3>
            </v-card-title>
            <v-card-text>Select Categories to display only matching {{ strollWordingPlural|capitalize }} on the map.</v-card-text>
            <v-list>
              <v-list-tile
                v-for="category in categories"
                :key="category.id"
                :style="{backgroundColor: category.colour, color: 'white'}"
                avatar
                @click="toggleCategorySelected(category.id)"
              >
                <v-list-tile-avatar>
                  <v-icon dark>{{ category.icon }}</v-icon>
                </v-list-tile-avatar>

                <v-list-tile-content>
                  <v-list-tile-title>{{ category.name }}</v-list-tile-title>
                </v-list-tile-content>

                <v-list-tile-action>
                  <v-icon v-if="isCategoryActive(category)" color="white">check_box</v-icon>
                  <v-icon v-else color="white">check_box_outline_blank</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
            <v-card-actions>
              <v-btn flat color="primary" @click="() => showCategories = false">Go</v-btn>
            </v-card-actions>
          </v-card>
        </v-bottom-sheet>

        <l-map
          v-if="settings"
          :center="liveMapCenter"
          :options="mapOptions"
          :zoom="liveMapZoom"
          :max-zoom="mapMaxZoom"
          style="flex-grow: 1;"
          ref="map"
          @update:center="updateLiveCenter"
          @update:zoom="updateLiveZoom"
        >
          <l-tile-layer
            :url="url"
            :attribution="attribution"
            :options="{useCache: true, maxZoom: mapMaxZoom}"
          />
          <l-circle-marker
            v-if="location"
            :lat-lng="[location.latitude, location.longitude]"
            :radius="6"
            :fill="true"
            :fill-opacity="0.3"
          />
          <v-marker-cluster :options="{disableClusteringAtZoom: 16, spiderfyOnMaxZoom: false}">
            <l-marker
              v-for="stroll in filteredStrolls"
              :key="stroll.id"
              :title="stroll.title"
              :lat-lng="stroll.point.coordinates"
              :icon="buildIconForStroll(stroll)"
              @click="clickMarker(stroll)"
            />
          </v-marker-cluster>
          <l-control-zoom :position="zoomPosition" />
        </l-map>
        <v-fab-transition>
          <v-btn
            v-show="location"
            color="white"
            class="my-location-fab"
            small
            absolute
            bottom
            right
            fab
            @click="centreMapOnCurrentLocation"
          >
            <v-icon>my_location</v-icon>
          </v-btn>
        </v-fab-transition>
      </v-layout>
    </v-slide-y-transition>
  </v-container>
</template>

<script>
import L from "leaflet";
import {
  LCircleMarker,
  LControlZoom,
  LMap,
  LMarker,
  LTileLayer
} from "vue2-leaflet";
import { mapActions, mapGetters, mapMutations } from "vuex";
import "leaflet.tilelayer.pouchdbcached";
import { leafletDefaults } from "@/constants/leaflet";
import PouchDB from "pouchdb";
import Search from "@/components/Search.vue";
import StrollPreview from "@/components/StrollPreview.vue";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";

export default {
  name: "Home",

  mounted() {
    if(localStorage.viewedDisclaimer) this.disclaimerDialog = false;
  },

  components: {
    LCircleMarker,
    LControlZoom,
    LMap,
    LTileLayer,
    LMarker,
    Search,
    StrollPreview,
    "v-marker-cluster": Vue2LeafletMarkerCluster
  },

  props: {
    showStrollPreview: {
      type: [Boolean],
      default: false
    },
    strollId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      ...leafletDefaults,
      mapOptions: {
        zoomControl: false
      },
      showCategories: false,
      activeStroll: null,
      disclaimerDialog: true
    };
  },

  watch: {
    strollId: async function() {
      this.activeStroll = await this.fetchStroll({ id: this.strollId });
    },
    // when we close the preview we update the url
    showStrollPreview: function(val) {
      !val && this.$router.push({ name: "home" });
    }
  },

  computed: {
    ...mapGetters({
      activeCategories: "getActiveCategories",
      categories: "getCategories",
      location: "getLocation",
      settings: "getSettings",
      settingsMapCentre: "getMapCentre",
      settingsMapZoomLevel: "getMapZoomLevel",
      mapMaxZoom: "getMapMaxZoom",
      strolls: "getStrolls",
      strollWording: "getStrollWording",
      strollWordingPlural: "getStrollWordingPlural",
      getCentre: "getCentre",
      getZoom: "getZoom",
      hasDisclaimer: "getHasDisclaimer",
      disclaimer: "getDisclaimer"
    }),

    liveMapCenter() {
      if (this.getCentre) {
        return this.getCentre;
      }
      return this.settingsMapCentre;
    },

    liveMapZoom() {
      if (this.getZoom) {
        return this.getZoom;
      }
      return this.settingsMapZoomLevel;
    },

    filteredStrolls() {
      if (!this.activeCategories.length) {
        return this.strolls;
      }
      return this.strolls.filter(s =>
        s.categories.map(c => c.id).some(c => this.activeCategories.includes(c))
      );
    }
  },

  async created() {
    if (window !== undefined) {
      window.PouchDB = PouchDB;
    }

    if (!this.location.latitude && !this.location.longitude) {
      await this.fetchLocation();
      await this.getCurrentPosition();
    }

    if (this.strollId) this.activeStroll = await this.fetchStroll({ id: this.strollId });
  },

  methods: {
    ...mapActions([
      "fetchLocation",
      "fetchMaps",
      "fetchCheckpoints",
      "fetchStroll",
      "getCurrentPosition",
      "setCentre",
      "setZoom"
    ]),

    ...mapMutations([
      "addActiveCategory",
      "clearActiveCategories",
      "removeActiveCategory"
    ]),

    closeDisclaimer() {
      localStorage.viewedDisclaimer = true;
      this.disclaimerDialog = false;
    },

    updateLiveCenter(val) {
      // console.log("my new center: " + val);
      this.setCentre(val);
    },

    updateLiveZoom(val) {
      // console.log("my new zoom: " + val);
      this.setZoom(val);
    },

    buildIconForStroll(stroll) {
      return L.IconMaterial.icon({
        icon: stroll.marker_icon,
        markerColor: stroll.marker_colour
      });
    },

    clickMarker(stroll) {
      // We prematurely set the active stroll here
      // to remove jitters when opening a new preview
      this.activeStroll = stroll;

      this.$router.push({
        name: "preview",
        params: {
          strollId: stroll.id
        }
      });
    },

    centreMapOnCurrentLocation() {
      this.centre = L.latLng(this.location.latitude, this.location.longitude);
      this.$refs.map.mapObject.flyTo(this.centre, 18);
    },

    toggleCategorySelected(categoryId) {
      if (this.activeCategories.includes(categoryId)) {
        this.removeActiveCategory({ categoryId });
      } else {
        this.addActiveCategory({ categoryId });
      }
    },

    isCategoryActive(category) {
      return this.activeCategories.includes(category.id);
    },

    isStrollFilteredOut(stroll) {
      if (!this.activeCategories.length) {
        return false;
      }
      const strollCategoryIds = stroll.categories.map(c => c.id);
      return strollCategoryIds.some(c => this.activeCategories.includes(c));
    }
  }
};
</script>

<style lang="sass">
@import "~leaflet.markercluster/dist/MarkerCluster.css"
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css"

.v-btn.my-location-fab
  bottom: 2.5rem !important

.v-bottom-sheet.v-dialog.v-dialog--active
  overflow-y: auto

.search-filter-container
  box-sizing: border-box
  flex-direction: column
  max-width: 1100px
  position: absolute
  top: 0
  width: 100%
  z-index: 3
</style>
